import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import md5 from "blueimp-md5";
import { Bell, Menu, User, Settings, LogOut, ChevronDown, Home, Star, Building } from "lucide-react";
import { useSelector } from "react-redux";
import io from "socket.io-client";
import { MdReport } from "react-icons/md";
import { getStartupNotifications, markNotificationAsRead } from '../utils/api';
import { isGuest } from '../utils/isGuest';

export default function ModernHeader({ toggleSidebar, isMobile }) {
  const [showProfile, setShowProfile] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  const dropdownRef = useRef(null);
  const notificationsRef = useRef(null);
  const navigate = useNavigate();

  const startupId = localStorage.getItem("startupId");
  const logoUrl = useSelector((state) => state.logo.logoUrl);
  const socketRef = useRef(null);
  const [imageError, setImageError] = useState(false);

  const isGuestUser = isGuest(); // 👈 guest mode check

  // Fetch profile only if not guest
  const { data: startupData } = useQuery({
    queryKey: ["startup-profile", startupId],
    queryFn: async () => {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/startups/${startupId}`
      );
      return response.data;
    },
    enabled: !!startupId && !isGuestUser, // 👈 disable API calls for guest
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
  });

  // Display values
  const fullName = isGuestUser ? "Guest User" : startupData?.startupName || "";
  const email = isGuestUser ? "guest@example.com" : startupData?.email || "";
  const username = isGuestUser ? "Guest" : startupData?.username || fullName || "";
  const displayInitials = (username || "").slice(0, 2).toUpperCase();

  const gravatarHash = email ? md5(email.trim().toLowerCase()) : null;
  const gravatarUrl = gravatarHash
    ? `https://www.gravatar.com/avatar/${gravatarHash}?s=40&d=identicon`
    : null;

  // Enable socket only if not guest
  useEffect(() => {
    if (isGuestUser) return;

    socketRef.current = io(process.env.REACT_APP_API_URL, {
      transports: ["websocket"],
      auth: { token: localStorage.getItem("startupToken") },
    });

    socketRef.current.on("newNotification", (notification) => {
      setUnreadCount(prev => prev + 1);
      setNotifications(prev => [notification, ...prev]);
    });

    return () => {
      socketRef.current?.disconnect();
    };
  }, [isGuestUser]);

  // Fetch notifications
  useEffect(() => {
    if (isGuestUser) return;

    const fetchNotifications = async () => {
      try {
        const response = await getStartupNotifications();
        setNotifications(response.data || []);
        setUnreadCount((response.data || []).filter(n => !n.read).length);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchNotifications();
  }, [isGuestUser]);

  const handleLogout = async () => {
    setIsLoggingOut(true);

    if (!isGuestUser) {
      try {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/logout`);
      } catch (error) {
        console.error("Logout failed:", error);
      }
    }

    localStorage.clear();
    navigate("/");
    setIsLoggingOut(false);
  };

  const markAsRead = async (notificationId, formId) => {
    console.log("Notification fetched!")
    if (isGuestUser) return; // 👈 prevent guest from reading notifications

    try {
      const response=await markNotificationAsRead(notificationId);
      console.log(response)
      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => prev - 1);
      navigate(`/admin-ca/chat/${formId}`);
      setShowNotifications(false);
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };
  useEffect(()=>{
    console.log(isGuest())
  },[])

  return (
    <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200 shadow-sm sticky top-0 z-30">
      <div className="flex items-center justify-between px-4 py-3">
        
        {/* Left */}
        <div className="flex items-center space-x-3">
          {isMobile && (
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}

          <h1 className="text-xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
            {isGuestUser ? "Guest Dashboard" : "Your Startup Dashboard"}
          </h1>
        </div>

        {/* Right */}
        <div className="flex items-center space-x-4">

          {/* Reports (enabled) */}
          <Link
            to="/reports"
            className="flex items-center gap-1 p-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-600"
          >
            <MdReport className="w-5 h-5 text-red-600" />
            <span className="hidden sm:block text-sm font-semibold">Help & Support</span>
          </Link>

          {/* Notifications — DISABLED for guest */}
          {!isGuestUser && (
            <div className="relative" ref={notificationsRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 rounded-lg bg-slate-50 hover:bg-slate-100 text-slate-600"
              >
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border z-50 max-h-96 overflow-y-auto">
                  <div className="p-3 border-b">
                    <h3 className="font-medium text-slate-800">Notifications</h3>
                  </div>

                  <div className="divide-y">
                    {notifications.length ? (
                      notifications.map(notification => (
                        <div
                          key={notification._id}
                          className={`p-3 hover:bg-slate-50 cursor-pointer ${!notification.read ? "bg-blue-50" : ""}`}
                          onClick={() => markAsRead(notification._id, notification.metadata.formId)}
                        >
                          <p className="text-sm">{notification.message}</p>
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(notification.createdAt).toLocaleString()}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-sm text-slate-500">
                        No notifications
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

    <Link
  to={isGuestUser ? "https://tactos.ssdspvhub.com/register-company" : "https://tactos.ssdspvhub.com/register-company"}
  className="flex items-center gap-1 px-3 py-2 rounded-lg 
             bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600
             text-white text-sm font-medium shadow 
             transition-all duration-800 ease-out hover:scale-105 
             hover:shadow-amber-500/40 hover:shadow-lg active:scale-95 animate-pulse"
>
  <Building className="w-4 h-4"/>
  {/* <Star  /> */}
  <span className="hidden sm:inline">Access Startup Portal</span>
</Link>



          {/* CRM – enabled */}
          <Link
            to={isGuestUser?"/login":"/crm"}
            className="flex items-center gap-1 px-3 py-2 rounded-lg bg-gradient-to-r from-yellow-400 to-yellow-600 text-white text-sm font-medium shadow"
          >
            <Star className="w-4 h-4" />
            <span className="hidden sm:inline">Access CRM</span>
          </Link>

          {/* Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowProfile(!showProfile)}
              className="flex items-center space-x-2 p-2 rounded-lg hover:bg-slate-50"
            >
              <div className="w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center bg-slate-100">
                {(!imageError && (logoUrl || gravatarUrl)) ? (
                  <img
                    src={logoUrl || gravatarUrl}
                    alt="Profile"
                    className="w-full h-full object-cover rounded-lg"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <span className="text-sm font-semibold">{displayInitials}</span>
                )}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-medium truncate max-w-20">{fullName}</p>
                <p className="text-xs text-slate-500">{isGuestUser ? "Guest Mode" : "Admin"}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400 hidden xs:block" />
            </button>

            {showProfile && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border py-2 z-50">
                <div className="px-4 py-3 border-b">
                  <p className="font-medium truncate">{fullName}</p>
                  <p className="text-xs text-slate-500 truncate">{email}</p>
                </div>

                {/* Allowed pages for guest */}
                <div className="py-2">
                  <Link to="/" className="flex items-center px-4 py-2 text-sm hover:bg-slate-50">
                    <Home className="w-4 h-4 mr-3" /> Home
                  </Link>

                  {!isGuestUser && (
                    <>
                      <Link to="/profile" className="flex items-center px-4 py-2 text-sm hover:bg-slate-50">
                        <User className="w-4 h-4 mr-3" /> Profile
                      </Link>
                      <Link to="/admin-setting" className="flex items-center px-4 py-2 text-sm hover:bg-slate-50">
                        <Settings className="w-4 h-4 mr-3" /> Settings
                      </Link>
                    </>
                  )}
                </div>

                <div className="border-t py-2">
                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    {isLoggingOut ? "Signing out..." : "Sign out"}
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </header>
  );
}
